import moment from 'moment';

/**
 * Valida un objeto contra las reglas definidas en el esquema.
 * @param {Object} objeto - El objeto a validar (empleado, novedad, etc)
 * @param {number} lineaIndex - Número de línea en el Excel
 * @param {Object} contexto - Contexto de validación (mapas, etc)
 * @param {Array} esquema - El esquema a usar (empleadoSchema, novedadSchema, etc)
 * @returns {Array<Object>} Lista de errores encontrados.
 */
export const validarEsquemas = (empleado, lineaIndex, contexto, esquema) => {
  return esquema.flatMap(regla =>
    validarRegla(regla, empleado, lineaIndex, contexto)
  );
};

const esCampoVacio = valor =>
  valor === null || valor === undefined || valor === '';

const construirError = (linea, detalle) => ({ linea, detalle });

const esEnteroValido = valor =>
  typeof valor === 'number' &&
  Number.isFinite(valor) &&
  Number.isInteger(valor);

const validadoresTipo = {
  String: (valor, nombre, linea) =>
    typeof valor === 'string'
      ? null
      : construirError(linea, `El campo '${nombre}' debe ser texto`),

  Integer: (valor, nombre, linea) => {
    if (!esEnteroValido(valor)) {
      return construirError(
        linea,
        `El campo '${nombre}' debe ser un número entero`
      );
    }
    return null;
  },

  Date: (valor, nombre, linea) =>
    moment(valor, 'DD/MM/YYYY', true).isValid()
      ? null
      : construirError(
          linea,
          `El campo '${nombre}' debe ser una fecha válida (DD/MM/YYYY)`
        ),

  Enum: (valor, nombre, linea, mapa, opciones) => {
    // Caso 1 — mapa dinámico (TipoDocumento, TipoContrato)
    if (mapa) {
      const esValido = [...mapa.values()].includes(valor) || mapa.has(valor);
      return esValido
        ? null
        : construirError(linea, `El campo '${nombre}' no es válido.`);
    }

    // Caso 2 — opciones estáticas (frecuenciaPago, tipoCuentaBancaria)
    if (opciones) {
      return opciones.includes(valor)
        ? null
        : construirError(linea, `El campo '${nombre}' no es válido.`);
    }

    return null;
  },
};

const validarLimitesNumericos = (regla, valor, lineaIndex) => {
  const errores = [];
  const esTipoNumerico = regla.type === 'Number' || regla.type === 'Integer';

  if (!esTipoNumerico) return errores;

  if (regla.min !== undefined && valor < regla.min) {
    errores.push(
      construirError(
        lineaIndex,
        `El campo '${regla.name}' no puede ser menor a ${regla.min}`
      )
    );
  }

  if (regla.max !== undefined && valor > regla.max) {
    errores.push(
      construirError(
        lineaIndex,
        `El campo '${regla.name}' no puede ser mayor a ${regla.max}`
      )
    );
  }

  return errores;
};

const validarLimitesLongitud = (regla, valor, lineaIndex) => {
  const errores = [];
  const longitud = valor?.toString().length || 0;

  if (regla.maxlength && longitud > regla.maxlength) {
    errores.push(
      construirError(
        lineaIndex,
        `La longitud de '${regla.name}' debe ser menor o igual a ${regla.maxlength}`
      )
    );
  }

  if (regla.minlength && longitud < regla.minlength) {
    errores.push(
      construirError(
        lineaIndex,
        `La longitud de '${regla.name}' debe ser mayor o igual a ${regla.minlength}`
      )
    );
  }

  return errores;
};

const validarRegla = (regla, empleado, lineaIndex, contexto) => {
  const valor = empleado[regla.name];

  if (regla.required && esCampoVacio(valor)) {
    return [
      construirError(lineaIndex, `El campo '${regla.name}' no puede ir vacío`),
    ];
  }

  if (esCampoVacio(valor)) return [];

  const validador = validadoresTipo[regla.type];

  if (validador) {
    const mapa = contexto?.mapas?.[regla.name];
    const errorTipo = validador(
      valor,
      regla.name,
      lineaIndex,
      mapa,
      regla.options
    );

    if (errorTipo) return [errorTipo];
  }

  return [
    ...validarLimitesNumericos(regla, valor, lineaIndex),
    ...validarLimitesLongitud(regla, valor, lineaIndex),
  ];
};
